import { config } from 'lib-shared-backend-config';

import { getAuthData } from './auth';

/** Represents the request method. */
export type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export type ApiRequest = {
  /** Relative path. */
  readonly path: string;

  /** The method. */
  readonly method?: ApiMethod;

  /** The payload data. */
  // readonly data?: unknown;

  /** The url search parameters. */
  readonly params?: Record<string, string | boolean | number | undefined>;
};

export interface GetItemsResponse<Data> {
  data: Data;
  meta: {
    next_page: string | null;
    prev_page: string | null;
  };
}

export type FetcherExtraArgs<TRequest = unknown> = {
  body?: TRequest;
  streamingFunction?: (response: Response) => Promise<void>;
};

type SwrArg<TRequest = unknown> = {
  arg: FetcherExtraArgs<TRequest>;
};

export type FetcherFunction = <TResponse = unknown, TRequest = unknown>(
  requestDetails: ApiRequest | string,
  args?: SwrArg<TRequest>,
) => Promise<TResponse>;

export let fetcherFunction: FetcherFunction;
export function createRestClient(): FetcherFunction {
  if (fetcherFunction) return fetcherFunction;

  fetcherFunction = async function restSend<
    TResponse = unknown,
    TRequest = unknown,
  >(
    requestDetails: ApiRequest | string,
    extraArgs?: SwrArg<TRequest>,
  ): Promise<TResponse> {
    const authData = await getAuthData();

    const response = await fetch(
      getUrl({
        path:
          typeof requestDetails === 'string'
            ? requestDetails
            : requestDetails.path,
        params:
          typeof requestDetails === 'string'
            ? undefined
            : requestDetails.params,
      }),
      {
        mode: 'cors',
        method:
          typeof requestDetails === 'string' || !requestDetails.method
            ? 'GET'
            : requestDetails.method,
        headers: {
          'content-type': 'application/json',
          authorization: `Basic ${window.btoa(
            `token:${authData?.access_token}`,
          )}`,
        },
        ...(typeof requestDetails !== 'string' &&
        requestDetails.method !== 'GET' &&
        extraArgs?.arg?.body
          ? { body: JSON.stringify(extraArgs.arg.body) }
          : {}),
      },
    );

    function throwError(responseBody: unknown) {
      const error = new Error('An error occured during REST request.');
      error.cause = {
        errors: (responseBody as { errors?: string[] })?.errors ?? [],
        statusCode: response.status,
      };

      throw error;
    }

    if (extraArgs?.arg.streamingFunction) {
      if (!response.ok) {
        const responseBody = (await response.json()) as unknown;
        throwError(responseBody);
      }
      await extraArgs.arg.streamingFunction(response);
      // when streaming we shouldn't wait for any response
      return undefined as TResponse;
    }

    const responseBody = (await response.json()) as unknown;

    if (!response.ok) {
      throwError(responseBody);
    }

    return responseBody as TResponse;
  };

  return fetcherFunction;
}

export function getUrl({
  path,
  params,
}: {
  path: string;
  readonly params?: Record<string, string | boolean | number | undefined>;
}): string {
  const queryString = params
    ? // eslint-disable-next-line
      // @ts-ignore
      '?' + new URLSearchParams(params)
    : '';

  return `${config.url.rest}/${path}${queryString}`;
}
